import { Award01Icon } from '@hugeicons/react'
import clsx from 'clsx'
import type { ReactNode } from 'react'

import { GameIcon } from './GameIcon'

export type RewardBadgeType = 'xp' | 'coins' | 'stamp'

const CAPSULE_ICONS: Record<RewardBadgeType, ReactNode> = {
  xp: <GameIcon name="star" size={16} />,
  coins: <GameIcon name="coins" size={16} />,
  stamp: <Award01Icon size={13} variant="duotone" className="text-info" />
}

const INLINE_ICONS: Record<RewardBadgeType, ReactNode> = {
  xp: <GameIcon name="star" size={12} />,
  coins: <GameIcon name="coins" size={12} />,
  stamp: <Award01Icon size={12} variant="duotone" className="text-info" />
}

const DEFAULT_LABELS: Record<RewardBadgeType, string> = {
  xp: 'XP',
  coins: 'moedas',
  stamp: 'selo'
}

export type RewardBadgeVariant = 'capsule' | 'inline'

export type RewardBadgeProps = {
  type: RewardBadgeType
  value: number | string
  label?: string
  variant?: RewardBadgeVariant
  icon?: ReactNode
  className?: string
}

/**
 * Cápsula de recompensa — experiência, moedas ou selo, com ícone + valor + label.
 * `variant="capsule"` (fundo `#f1f3f8`, standalone) ou `variant="inline"`
 * (ícone + texto sem fundo, dentro de cards de jornada/missão). `xp` e `coins`
 * usam PNG customizado via `GameIcon` (não SVG genérico) — a arte da moeda/estrela
 * é intencional, reforça a metáfora de jogo. Use `RewardBadge.Group` para
 * agrupar múltiplas recompensas em linha com gap consistente.
 *
 * Gap conhecido vs. handoff: `RewardBadge.Dual` (xp + coins num único badge
 * combinado) ainda não foi portado — ver MASTER.md §0.5.
 *
 * @example
 * <RewardBadge.Group>
 *   <RewardBadge type="xp" value={150} variant="inline" />
 *   <RewardBadge type="coins" value={50} variant="inline" />
 * </RewardBadge.Group>
 */
export const RewardBadge = ({
  type,
  value,
  label,
  variant = 'capsule',
  icon,
  className
}: RewardBadgeProps) => {
  const iconNode =
    icon ?? (variant === 'inline' ? INLINE_ICONS[type] : CAPSULE_ICONS[type])
  const labelText = label ?? DEFAULT_LABELS[type]

  return (
    <span
      className={clsx(
        'reward-badge',
        variant === 'inline' && 'reward-badge--inline',
        variant === 'inline' && `reward-badge--${type}`,
        className
      )}
    >
      <span className="reward-badge__icon">{iconNode}</span>
      <span className="reward-badge__value">{value}</span>
      <span className="reward-badge__label">{labelText}</span>
    </span>
  )
}

export type RewardBadgeGroupProps = {
  children: ReactNode
  className?: string
}

const RewardBadgeGroup = ({ children, className }: RewardBadgeGroupProps) => (
  <div className={clsx('reward-badge-group', className)}>{children}</div>
)

RewardBadgeGroup.displayName = 'RewardBadgeGroup'

RewardBadge.Group = RewardBadgeGroup
RewardBadge.displayName = 'RewardBadge'
